// MyVouchers.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_URL = 'https://collabbay.com/APIControllers/FetchCreatorVouchers.php';
const SESSION_URL = 'https://collabbay.com/APIControllers/GetSessionData.php';

const LOGO_URL = 'https://collabbay.com/CollabbayLogoDark.png';
const VOUCHER_BASE_URL = 'https://collabbay.com/Voucher-';

const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg';

const CREATOR_EARNINGS_PER_STOCK = 5;

const STOCK_SORT_OPTIONS = [
  { key: 'latest', label: 'Latest' },
  { key: 'stock_high', label: 'Most stock' },
  { key: 'potential_high', label: 'Highest potential' },
  { key: 'sales_high', label: 'Most purchases' },
  { key: 'brand_az', label: 'Brand A-Z' },
];

const SALES_SORT_OPTIONS = [
  { key: 'latest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'amount_high', label: 'Highest amount' },
  { key: 'brand_az', label: 'Brand A-Z' },
];

const normaliseImageUri = (image) => {
  if (!image || !String(image).trim()) return FALLBACK_IMAGE;

  const trimmed = String(image).trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `https://collabbay.com${trimmed}`;
  }

  return `https://collabbay.com/${trimmed}`;
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('en-GB') : '0';
};

const formatMoney = (value) => {
  const n = Number(value || 0);

  if (!Number.isFinite(n)) return '£0';

  return `£${n.toLocaleString('en-GB', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const toTimestamp = (value) => {
  if (!value) return 0;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return 0;

  return date.getTime();
};

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTime = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDealText = (voucher) => {
  const spend = Number(voucher?.OnSpend || 0);
  const discount = Number(voucher?.Discount || 0);

  if (spend > 0 && discount > 0) {
    return `${discount}% off when spending £${spend}`;
  }

  if (discount > 0) {
    return `${discount}% off`;
  }

  return 'Voucher offer';
};

const getBrandName = (item) => {
  return item?.DisplayBrandName || item?.BrandName || 'Brand';
};

const getCurrentStock = (voucher) => {
  if (voucher?.CurrentStock !== undefined && voucher?.CurrentStock !== null) {
    return Math.max(Number(voucher.CurrentStock || 0), 0);
  }

  if (voucher?.Stock !== undefined && voucher?.Stock !== null) {
    return Math.max(Number(voucher.Stock || 0), 0);
  }

  if (voucher?.Remaining !== undefined && voucher?.Remaining !== null) {
    return Math.max(Number(voucher.Remaining || 0), 0);
  }

  return Math.max(Number(voucher?.StockAssigned || 0), 0);
};

const getPurchaseCount = (voucher) => {
  return Number(voucher?.PurchaseCount || voucher?.Redeemed || 0);
};

const getTotalAmountPaid = (voucher) => {
  return Number(voucher?.TotalAmountPaid || 0);
};

const getPurchaseDate = (purchase) => {
  return purchase?.DateTimePurchased || purchase?.CreatedAt || purchase?.Date;
};

const getVoucherUrl = (voucher) => {
  const id = voucher?.CollabID || '';
  return `${VOUCHER_BASE_URL}${encodeURIComponent(String(id))}`;
};

const getStockKey = (voucher, index) => {
  return `${voucher?.CollabID || 'voucher'}-${voucher?.BrandName || 'brand'}-${index}`;
};

export default function MyVouchers({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserData] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({
    TotalDeals: 0,
    TotalCurrentStock: 0,
    TotalPurchases: 0,
    TotalAmountPaid: 0,
    PotentialCreatorEarnings: 0,
  });

  const [activeTab, setActiveTab] = useState('stock');
  const [stockSort, setStockSort] = useState('latest');
  const [salesSort, setSalesSort] = useState('latest');
  const [expandedStockKey, setExpandedStockKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await fetch(SESSION_URL, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      setUserData(data || {});
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  const fetchCreatorVouchers = async () => {
    const response = await fetch(`${API_URL}?d=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (error) {
      throw new Error(`Invalid voucher response: ${rawText.slice(0, 180)}`);
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || data.debug || 'Could not load vouchers.');
    }

    setVouchers(Array.isArray(data.vouchers) ? data.vouchers : []);

    setPurchases(
      Array.isArray(data.purchases)
        ? data.purchases
        : Array.isArray(data.ledger)
          ? data.ledger
          : []
    );

    setSummary(
      data.summary || {
        TotalDeals: 0,
        TotalCurrentStock: 0,
        TotalPurchases: 0,
        TotalAmountPaid: 0,
        PotentialCreatorEarnings: 0,
      }
    );

    setErrorMessage('');
  };

  const loadPage = async () => {
    try {
      await Promise.all([fetchUserData(), fetchCreatorVouchers()]);
    } catch (error) {
      console.error('Load creator vouchers error:', error);
      setErrorMessage(error.message || 'Could not load your vouchers.');
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await loadPage();
      setLoading(false);
    };

    run();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPage();
    setRefreshing(false);
  };

  const overview = useMemo(() => {
    const currentStock = vouchers.reduce(
      (sum, voucher) => sum + getCurrentStock(voucher),
      0
    );

    const purchaseCount = purchases.length || Number(summary.TotalPurchases || 0);

    const totalSales = purchases.reduce(
      (sum, item) => sum + Number(item.AmountPaid || 0),
      0
    );

    const potential = currentStock * CREATOR_EARNINGS_PER_STOCK;

    const latestPurchase = [...purchases].sort(
      (a, b) => toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a))
    )[0];

    return {
      currentStock,
      potential,
      purchaseCount,
      totalSales,
      activeBrands: vouchers.length,
      latestPurchaseDate: latestPurchase ? getPurchaseDate(latestPurchase) : null,
    };
  }, [vouchers, purchases, summary.TotalPurchases]);

  const sortedStock = useMemo(() => {
    const list = [...vouchers];

    list.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      const aStock = getCurrentStock(a);
      const bStock = getCurrentStock(b);

      const aPotential = aStock * CREATOR_EARNINGS_PER_STOCK;
      const bPotential = bStock * CREATOR_EARNINGS_PER_STOCK;

      const aSales = getPurchaseCount(a);
      const bSales = getPurchaseCount(b);

      if (stockSort === 'stock_high') return bStock - aStock;
      if (stockSort === 'potential_high') return bPotential - aPotential;
      if (stockSort === 'sales_high') return bSales - aSales;
      if (stockSort === 'brand_az') return aBrand.localeCompare(bBrand);

      const aDate = toTimestamp(a.LastUpdatedAt || a.FirstAssignedAt);
      const bDate = toTimestamp(b.LastUpdatedAt || b.FirstAssignedAt);

      return bDate - aDate;
    });

    return list;
  }, [vouchers, stockSort]);

  const sortedPurchases = useMemo(() => {
    const list = [...purchases];

    list.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      const aAmount = Number(a.AmountPaid || 0);
      const bAmount = Number(b.AmountPaid || 0);

      if (salesSort === 'oldest') {
        return toTimestamp(getPurchaseDate(a)) - toTimestamp(getPurchaseDate(b));
      }

      if (salesSort === 'amount_high') {
        return bAmount - aAmount;
      }

      if (salesSort === 'brand_az') {
        return aBrand.localeCompare(bBrand);
      }

      return toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a));
    });

    return list;
  }, [purchases, salesSort]);

  const handleFooterPress = (routeName, fallbackUrl = null) => {
    if (routeName && navigation?.navigate) {
      navigation.navigate(routeName);
      return;
    }

    if (fallbackUrl) {
      Linking.openURL(fallbackUrl);
    }
  };

  const openVoucherLink = async (voucher) => {
    const url = getVoucherUrl(voucher);

    try {
      await Linking.openURL(url);
    } catch (error) {
      Alert.alert('Voucher link', url);
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerLeft}>
        <Image source={{ uri: LOGO_URL }} style={styles.logo} />

        <View>
          <Text style={styles.headerTitle}>Voucher sales</Text>
          <Text style={styles.headerSub}>
            {userData?.Username ? userData.Username : 'Creator dashboard'}
          </Text>
        </View>
      </View>

      <TouchableOpacity
        style={styles.helpButton}
        onPress={() =>
          Alert.alert(
            'Voucher dashboard',
            'Stock shows current live voucher batches. Sales shows actual purchases from voucherpurchases.'
          )
        }
        activeOpacity={0.75}
      >
        <Ionicons name="help-circle-outline" size={21} color="#697386" />
      </TouchableOpacity>
    </View>
  );

  const renderOverviewMetric = (label, value, helper = null) => (
    <View style={styles.overviewMetric}>
      <Text style={styles.overviewMetricLabel}>{label}</Text>
      <Text style={styles.overviewMetricValue}>{value}</Text>
      {helper ? <Text style={styles.overviewMetricHelper}>{helper}</Text> : null}
    </View>
  );

  const renderOverview = () => (
    <View style={styles.overviewPanel}>
      <View style={styles.overviewTop}>
        <View>
          <Text style={styles.overviewLabel}>Total sales</Text>
          <Text style={styles.overviewValue}>
            {formatMoney(overview.totalSales)}
          </Text>
          <Text style={styles.overviewHelper}>
            {overview.latestPurchaseDate
              ? `Latest purchase ${formatDate(overview.latestPurchaseDate)}`
              : 'No purchases recorded yet'}
          </Text>
        </View>

        <View style={styles.softBadge}>
          <Text style={styles.softBadgeText}>Live</Text>
        </View>
      </View>

      <View style={styles.overviewLine} />

      <View style={styles.overviewGrid}>
        {renderOverviewMetric('Live stock', formatNumber(overview.currentStock), 'available')}
        {renderOverviewMetric('Potential', formatMoney(overview.potential), 'stock × £5')}
        {renderOverviewMetric('Brands', formatNumber(overview.activeBrands), 'active')}
        {renderOverviewMetric('Purchases', formatNumber(overview.purchaseCount), 'orders')}
      </View>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabShell}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'stock' && styles.tabActive]}
        onPress={() => setActiveTab('stock')}
        activeOpacity={0.85}
      >
        <Text style={[styles.tabText, activeTab === 'stock' && styles.tabTextActive]}>
          Stock
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'sales' && styles.tabActive]}
        onPress={() => setActiveTab('sales')}
        activeOpacity={0.85}
      >
        <Text style={[styles.tabText, activeTab === 'sales' && styles.tabTextActive]}>
          Sales
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderSortChips = (options, activeValue, onChange) => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.sortRow}
    >
      {options.map((option) => {
        const active = activeValue === option.key;

        return (
          <TouchableOpacity
            key={option.key}
            style={[styles.sortChip, active && styles.sortChipActive]}
            onPress={() => onChange(option.key)}
            activeOpacity={0.78}
          >
            <Text style={[styles.sortChipText, active && styles.sortChipTextActive]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const renderEmptyState = (icon, title, body) => (
    <View style={styles.emptyPanel}>
      <View style={styles.emptyIcon}>
        <Ionicons name={icon} size={24} color="#697386" />
      </View>
      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptyBody}>{body}</Text>
    </View>
  );

  const renderSectionIntro = (title, subtitle) => (
    <View style={styles.sectionIntro}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.sectionSubtitle}>{subtitle}</Text>
    </View>
  );

  const renderStockRow = (voucher, index) => {
    const key = getStockKey(voucher, index);
    const expanded = expandedStockKey === key;

    const brandImage = normaliseImageUri(voucher.BrandProfileImage);
    const brandName = getBrandName(voucher);
    const currentStock = getCurrentStock(voucher);
    const purchaseCount = getPurchaseCount(voucher);
    const amountSold = getTotalAmountPaid(voucher);
    const potential = currentStock * CREATOR_EARNINGS_PER_STOCK;
    const latestDate = voucher.LastPurchasedAt || voucher.LastUpdatedAt || voucher.FirstAssignedAt;

    return (
      <View key={key} style={styles.stockRowWrap}>
        <TouchableOpacity
          style={styles.stockRow}
          activeOpacity={0.82}
          onPress={() => setExpandedStockKey(expanded ? null : key)}
        >
          <Image source={{ uri: brandImage }} style={styles.brandImage} />

          <View style={styles.stockBrandArea}>
            <Text style={styles.brandName} numberOfLines={1}>
              {brandName}
            </Text>

            <Text style={styles.brandDescription} numberOfLines={1}>
              {formatDealText(voucher)}
            </Text>

            <Text style={styles.rowMutedText} numberOfLines={1}>
              Updated {formatDate(latestDate)}
            </Text>
          </View>

          <View style={styles.stockValueArea}>
            <Text style={styles.rowPrimaryValue}>{formatNumber(currentStock)}</Text>
            <Text style={styles.rowMutedText}>left</Text>
          </View>

          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={17}
            color="#a3acb9"
            style={styles.rowChevron}
          />
        </TouchableOpacity>

        {expanded ? (
          <View style={styles.expandedPanel}>
            <View style={styles.expandedGrid}>
              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Current stock</Text>
                <Text style={styles.cleanMetricValue}>{formatNumber(currentStock)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Potential</Text>
                <Text style={styles.cleanMetricValue}>{formatMoney(potential)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Purchases</Text>
                <Text style={styles.cleanMetricValue}>{formatNumber(purchaseCount)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Sold</Text>
                <Text style={styles.cleanMetricValue}>{formatMoney(amountSold)}</Text>
              </View>
            </View>

            <View style={styles.detailRows}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Voucher page</Text>
                <Text style={styles.detailValue} numberOfLines={1}>
                  {getVoucherUrl(voucher).replace('https://', '')}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Created</Text>
                <Text style={styles.detailValue}>
                  {formatDate(voucher.FirstAssignedAt)}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Last update</Text>
                <Text style={styles.detailValue}>
                  {formatDateTime(voucher.LastUpdatedAt)}
                </Text>
              </View>
            </View>

            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => openVoucherLink(voucher)}
              activeOpacity={0.82}
            >
              <Text style={styles.linkButtonText}>View voucher sales link</Text>
              <Ionicons name="arrow-forward" size={16} color="#635bff" />
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  };

  const renderStockTab = () => (
    <View>
      {renderSectionIntro(
        'Live stock',
        'Tap a brand to expand voucher details and open its sales page.'
      )}

      {renderSortChips(STOCK_SORT_OPTIONS, stockSort, setStockSort)}

      {sortedStock.length === 0 ? (
        renderEmptyState(
          'ticket-outline',
          'No voucher stock yet',
          'When a venue assigns vouchers to you, they will appear here.'
        )
      ) : (
        <View style={styles.dataPanel}>
          <View style={styles.stockHeaderRow}>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Brand</Text>
            <Text style={styles.tableHeaderText}>Stock</Text>
          </View>

          {sortedStock.map((voucher, index) => renderStockRow(voucher, index))}
        </View>
      )}
    </View>
  );

  const renderPurchaseRow = (purchase, index) => {
    const brandImage = normaliseImageUri(purchase.BrandProfileImage);
    const brandName = getBrandName(purchase);
    const date = getPurchaseDate(purchase);

    return (
      <View
        key={`${purchase.VoucherID || 'purchase'}-${purchase.CollabID || 'collab'}-${index}`}
        style={styles.purchaseRow}
      >
        <View style={styles.purchaseDateArea}>
          <Text style={styles.purchaseDate}>{formatDate(date)}</Text>
          <Text style={styles.purchaseTime}>{formatTime(date)}</Text>
        </View>

        <Image source={{ uri: brandImage }} style={styles.purchaseImage} />

        <View style={styles.purchaseMain}>
          <Text style={styles.purchaseBrand} numberOfLines={1}>
            {brandName}
          </Text>

          <Text style={styles.purchaseCustomer} numberOfLines={1}>
            {purchase.CustomerEmail || 'Customer email unavailable'}
          </Text>

          <Text style={styles.purchaseReference} numberOfLines={1}>
            Passcode {purchase.VoucherPasscode || '—'} · Voucher #{purchase.VoucherID || '—'}
          </Text>
        </View>

        <View style={styles.purchaseAmountArea}>
          <Text style={styles.purchaseAmount}>
            {formatMoney(purchase.AmountPaid)}
          </Text>

          <View style={styles.paidPill}>
            <View style={styles.paidDot} />
            <Text style={styles.paidText}>Paid</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderSalesTab = () => (
    <View>
      {renderSectionIntro(
        'Sales ledger',
        'Actual customer purchases from voucherpurchases.'
      )}

      {renderSortChips(SALES_SORT_OPTIONS, salesSort, setSalesSort)}

      {sortedPurchases.length === 0 ? (
        renderEmptyState(
          'receipt-outline',
          'No voucher purchases yet',
          'When customers purchase vouchers through your sales links, they will appear here.'
        )
      ) : (
        <View style={styles.dataPanel}>
          <View style={styles.salesHeaderRow}>
            <Text style={[styles.tableHeaderText, { width: 82 }]}>Date</Text>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Customer</Text>
            <Text style={styles.tableHeaderText}>Amount</Text>
          </View>

          {sortedPurchases.map((purchase, index) => renderPurchaseRow(purchase, index))}
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#635bff" />
        <Text style={styles.loadingText}>Loading voucher dashboard...</Text>
      </View>
    );
  }

  return (
    <View style={styles.outerPage}>
      <SafeAreaView style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {renderHeader()}

          {errorMessage ? (
            <View style={styles.errorPanel}>
              <Text style={styles.errorTitle}>Could not load vouchers</Text>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          {renderOverview()}
          {renderTabs()}

          {activeTab === 'stock' ? renderStockTab() : renderSalesTab()}

          <TouchableOpacity
            style={styles.privacyLink}
            onPress={() =>
              Linking.openURL('https://collabbay.com/PrivacyPolicy.html')
            }
          >
            <Text style={styles.privacyText}>
              By using collabbay, you agree to our Privacy Policy.
            </Text>
          </TouchableOpacity>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('Homepage')}
          >
            <Ionicons name="home-outline" size={23} color="#697386" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('CollabCalendar')}
          >
            <Ionicons name="calendar-outline" size={23} color="#697386" />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleFooterPress('MyFeed')}
            style={styles.footerMainButton}
          >
            <Ionicons name="film-outline" size={21} color="#635bff" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserActivityFeed')}
          >
            <Ionicons name="chatbox-ellipses-outline" size={23} color="#697386" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserRecapPage')}
          >
            <Ionicons name="person-outline" size={23} color="#697386" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  outerPage: {
    flex: 1,
    backgroundColor: '#f6f9fc',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 720,
    backgroundColor: '#f6f9fc',
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingBottom: 112,
  },

  loadingPage: {
    flex: 1,
    backgroundColor: '#f6f9fc',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#697386',
    fontSize: 13,
    fontWeight: '400',
  },

  header: {
    paddingTop: 24,
    paddingBottom: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  logo: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e6ebf1',
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '400',
    color: '#1a1f36',
    letterSpacing: -0.4,
  },
  headerSub: {
    marginTop: 3,
    fontSize: 13,
    color: '#697386',
    fontWeight: '400',
  },
  helpButton: {
    width: 39,
    height: 39,
    borderRadius: 19.5,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e6ebf1',
    alignItems: 'center',
    justifyContent: 'center',
  },

  errorPanel: {
    backgroundColor: '#fff8f8',
    borderWidth: 1,
    borderColor: '#f5c2c7',
    borderRadius: 14,
    padding: 14,
    marginBottom: 14,
  },
  errorTitle: {
    color: '#842029',
    fontSize: 14,
    fontWeight: '400',
  },
  errorText: {
    color: '#842029',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 17,
    fontWeight: '400',
  },

  overviewPanel: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 18,
    borderWidth: 1,
    borderColor: '#e6ebf1',
    marginBottom: 14,
    shadowColor: '#1a1f36',
    shadowOpacity: 0.035,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 1,
  },
  overviewTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  overviewLabel: {
    fontSize: 13,
    color: '#697386',
    fontWeight: '400',
    marginBottom: 6,
  },
  overviewValue: {
    fontSize: 36,
    color: '#1a1f36',
    fontWeight: '400',
    letterSpacing: -1.2,
  },
  overviewHelper: {
    marginTop: 6,
    fontSize: 13,
    color: '#697386',
    lineHeight: 19,
    fontWeight: '400',
  },
  softBadge: {
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#ecfdf3',
    borderWidth: 1,
    borderColor: '#d3f5df',
  },
  softBadgeText: {
    color: '#16833a',
    fontSize: 12,
    fontWeight: '400',
  },
  overviewLine: {
    height: 1,
    backgroundColor: '#edf1f5',
    marginVertical: 16,
  },
  overviewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  overviewMetric: {
    width: '50%',
    paddingVertical: 8,
    paddingRight: 12,
  },
  overviewMetricLabel: {
    fontSize: 12,
    color: '#697386',
    fontWeight: '400',
    marginBottom: 5,
  },
  overviewMetricValue: {
    fontSize: 17,
    color: '#1a1f36',
    fontWeight: '400',
  },
  overviewMetricHelper: {
    marginTop: 3,
    fontSize: 11,
    color: '#a3acb9',
    fontWeight: '400',
  },

  tabShell: {
    flexDirection: 'row',
    backgroundColor: '#e9eef5',
    borderRadius: 11,
    padding: 3,
    marginBottom: 18,
  },
  tab: {
    flex: 1,
    borderRadius: 9,
    paddingVertical: 10,
    alignItems: 'center',
  },
  tabActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#1a1f36',
    shadowOpacity: 0.04,
    shadowRadius: 7,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  tabText: {
    fontSize: 13,
    color: '#697386',
    fontWeight: '400',
  },
  tabTextActive: {
    color: '#1a1f36',
    fontWeight: '400',
  },

  sectionIntro: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 19,
    color: '#1a1f36',
    fontWeight: '400',
    letterSpacing: -0.2,
  },
  sectionSubtitle: {
    marginTop: 5,
    fontSize: 13,
    color: '#697386',
    lineHeight: 19,
    fontWeight: '400',
  },

  sortRow: {
    paddingBottom: 13,
    gap: 8,
  },
  sortChip: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e6ebf1',
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  sortChipActive: {
    backgroundColor: '#f4f2ff',
    borderColor: '#d8d0ff',
  },
  sortChipText: {
    color: '#697386',
    fontSize: 12,
    fontWeight: '400',
  },
  sortChipTextActive: {
    color: '#635bff',
    fontWeight: '400',
  },

  dataPanel: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#e6ebf1',
    overflow: 'hidden',
    shadowColor: '#1a1f36',
    shadowOpacity: 0.03,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 5 },
    elevation: 1,
  },
  stockHeaderRow: {
    paddingHorizontal: 16,
    paddingVertical: 11,
    backgroundColor: '#fbfcfe',
    borderBottomWidth: 1,
    borderBottomColor: '#edf1f5',
    flexDirection: 'row',
    alignItems: 'center',
  },
  salesHeaderRow: {
    paddingHorizontal: 16,
    paddingVertical: 11,
    backgroundColor: '#fbfcfe',
    borderBottomWidth: 1,
    borderBottomColor: '#edf1f5',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableHeaderText: {
    fontSize: 11,
    color: '#8792a2',
    fontWeight: '400',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  stockRowWrap: {
    borderBottomWidth: 1,
    borderBottomColor: '#edf1f5',
  },
  stockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  brandImage: {
    width: 74,
    height: 74,
    borderRadius: 37,
    backgroundColor: '#edf1f5',
    borderWidth: 1,
    borderColor: '#e6ebf1',
    marginRight: 15,
  },
  stockBrandArea: {
    flex: 1,
    minWidth: 0,
  },
  brandName: {
    fontSize: 16,
    color: '#1a1f36',
    fontWeight: '400',
    letterSpacing: -0.1,
  },
  brandDescription: {
    marginTop: 5,
    fontSize: 13,
    color: '#697386',
    fontWeight: '400',
    lineHeight: 18,
  },
  rowMutedText: {
    marginTop: 4,
    fontSize: 12,
    color: '#a3acb9',
    fontWeight: '400',
  },
  stockValueArea: {
    alignItems: 'flex-end',
    minWidth: 44,
    marginLeft: 10,
  },
  rowPrimaryValue: {
    fontSize: 18,
    color: '#1a1f36',
    fontWeight: '400',
  },
  rowChevron: {
    marginLeft: 8,
  },

  expandedPanel: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  expandedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  cleanMetric: {
    width: '48.5%',
    backgroundColor: '#f6f9fc',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#edf1f5',
  },
  cleanMetricLabel: {
    fontSize: 12,
    color: '#697386',
    fontWeight: '400',
    marginBottom: 5,
  },
  cleanMetricValue: {
    fontSize: 16,
    color: '#1a1f36',
    fontWeight: '400',
  },
  detailRows: {
    borderTopWidth: 1,
    borderTopColor: '#edf1f5',
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#edf1f5',
  },
  detailLabel: {
    width: 98,
    fontSize: 12,
    color: '#8792a2',
    fontWeight: '400',
  },
  detailValue: {
    flex: 1,
    fontSize: 12,
    color: '#1a1f36',
    fontWeight: '400',
  },
  linkButton: {
    marginTop: 13,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d8d0ff',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  linkButtonText: {
    color: '#635bff',
    fontSize: 14,
    fontWeight: '400',
  },

  purchaseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#edf1f5',
  },
  purchaseDateArea: {
    width: 82,
  },
  purchaseDate: {
    fontSize: 12,
    color: '#1a1f36',
    fontWeight: '400',
  },
  purchaseTime: {
    marginTop: 3,
    fontSize: 11,
    color: '#a3acb9',
    fontWeight: '400',
  },
  purchaseImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#edf1f5',
    borderWidth: 1,
    borderColor: '#e6ebf1',
    marginRight: 12,
  },
  purchaseMain: {
    flex: 1,
    minWidth: 0,
  },
  purchaseBrand: {
    fontSize: 14,
    color: '#1a1f36',
    fontWeight: '400',
  },
  purchaseCustomer: {
    marginTop: 3,
    fontSize: 12,
    color: '#697386',
    fontWeight: '400',
  },
  purchaseReference: {
    marginTop: 3,
    fontSize: 11,
    color: '#a3acb9',
    fontWeight: '400',
  },
  purchaseAmountArea: {
    alignItems: 'flex-end',
    marginLeft: 8,
  },
  purchaseAmount: {
    fontSize: 15,
    color: '#1a1f36',
    fontWeight: '400',
  },
  paidPill: {
    marginTop: 5,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 7,
    backgroundColor: '#ecfdf3',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  paidDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#24b47e',
  },
  paidText: {
    fontSize: 10,
    color: '#16833a',
    fontWeight: '400',
  },

  emptyPanel: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#e6ebf1',
    padding: 28,
    alignItems: 'center',
  },
  emptyIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: '#f6f9fc',
    borderWidth: 1,
    borderColor: '#edf1f5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 17,
    color: '#1a1f36',
    fontWeight: '400',
    textAlign: 'center',
  },
  emptyBody: {
    marginTop: 6,
    fontSize: 13,
    color: '#697386',
    fontWeight: '400',
    lineHeight: 19,
    textAlign: 'center',
  },

  privacyLink: {
    marginTop: 24,
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 11,
    color: '#697386',
    textDecorationLine: 'underline',
    textAlign: 'center',
    fontWeight: '400',
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#ffffff',
    left: 0,
    right: 0,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#e6ebf1',
  },
  footerIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
  },
  footerMainButton: {
    backgroundColor: '#f4f2ff',
    padding: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#d8d0ff',
  },
});